import { CarouselItem } from '@wellesley/ui'
import {
  MediaItem,
  MediaItemProps,
  SelectItemProps,
} from 'components/MediaItem'
import SliderWithBottomControls from 'components/SliderWithBottomControls'
import { FC } from 'react'
import Placeholder from '../Placeholder'
import { ContentOverlayProps } from 'components/SensitiveContentOverlay'
import {
  isExtremeCase,
  mapUpload,
  MediaUpload,
  selectUploadFile,
} from '@wellesley/uploads'
import { useSelector } from 'react-redux'
import { platformSettingsSlice } from '@wellesley/settings'
import { FileDesc } from '@wellesley/openapi'
import { SMALL_PREVIEW_SIZE } from 'constants/ui'

const NAV_HEIGHT = 44
const MIN_HEIGHT = 240

type MultipleMediaProps = {
  attachments: MediaUpload[]
  containerWidth: number
  onClick: (props: SelectItemProps) => void
  isReply?: boolean
  isSmallPreview?: boolean
} & ContentOverlayProps &
  Pick<MediaItemProps, 'removeMedia' | 'updateMedia'>

const MultipleMedia: FC<MultipleMediaProps> = ({
  attachments: attachmentsFromProps,
  isReply,
  isSmallPreview,
  containerWidth,
  onClick,
  updateMedia,
  ...overlayProps
}) => {
  const attachments = attachmentsFromProps.map((a) => mapUpload(a))

  const videoExtensions = useSelector(
    platformSettingsSlice.selectors.selectVideoExtensions,
  )

  const files = attachments.map((upload) =>
    selectUploadFile({
      upload,
      allowedExtensions:
        upload?.uploadType === 'VIDEO' ? videoExtensions : undefined,
      size: containerWidth,
    }),
  )

  const halfOfWrapper = isReply
    ? Math.max(containerWidth / 2, MIN_HEIGHT)
    : containerWidth / 2

  const maxMediaHeight =
    files.sort((a, b) => (b?.meta.height || 0) - (a?.meta.height || 0))[0]?.meta
      .height || halfOfWrapper

  const mediaHeight = Math.min(halfOfWrapper, maxMediaHeight)

  const wrapperHeight = mediaHeight + NAV_HEIGHT

  const slideGap = isSmallPreview
    ? containerWidth * 0.01
    : containerWidth * 0.05

  const isSliderActive = isSmallPreview
    ? containerWidth < attachments.length * SMALL_PREVIEW_SIZE
    : attachments.length > 1

  const mediaCount = isSmallPreview
    ? Math.round(containerWidth / (SMALL_PREVIEW_SIZE * attachments.length)) + 1
    : attachments.length

  const isExtremeImage = isExtremeCase({
    meta: { height: maxMediaHeight },
  } as FileDesc)

  const height = !isSmallPreview && !isExtremeImage ? wrapperHeight : undefined

  return (
    <Placeholder
      height={height}
      className="relative flex max-h-full"
      testId="multipleMedia"
    >
      <SliderWithBottomControls
        mediaCount={mediaCount}
        opts={{
          active: isSliderActive,
        }}
        renderSlides={(current) => (
          <>
            {attachments.map((upload, index) => (
              <CarouselItem
                sideGap={slideGap}
                onClick={(e) => e.stopPropagation()}
                key={`${upload.uploadId}-${index}`}
                className="flex max-w-[100%] basis-[unset] items-center"
              >
                <MediaItem
                  upload={upload}
                  isSmallPreview={isSmallPreview}
                  onClick={onClick}
                  canPlayInline={true}
                  inView={isSliderActive ? current === index + 1 : true}
                  containerWidth={containerWidth}
                  mediaHeight={mediaHeight}
                  updateMedia={updateMedia}
                  {...overlayProps}
                />
              </CarouselItem>
            ))}
          </>
        )}
      />
    </Placeholder>
  )
}

export default MultipleMedia
